/**
 * Floor area helpers for EnergyPlus Mod JSON (9.4 + 23.1).
 * 23.1 often exports zone floor_area as "Autocalculate"; derive from Floor surfaces when needed.
 */

export function parseNumericValue(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function polygonArea3D(vertices: Array<{ x: number; y: number; z: number }>): number {
  const n = vertices.length;
  if (n < 3) return 0;

  let ax = 0;
  let ay = 0;
  let az = 0;
  for (let i = 0; i < n; i++) {
    const v1 = vertices[i];
    const v2 = vertices[(i + 1) % n];
    ax += (v1.y - v2.y) * (v1.z + v2.z);
    ay += (v1.z - v2.z) * (v1.x + v2.x);
    az += (v1.x - v2.x) * (v1.y + v2.y);
  }
  return 0.5 * Math.sqrt(ax * ax + ay * ay + az * az);
}

export function buildZoneFloorAreasFromSurfaces(
  data: Record<string, unknown> | null | undefined
): Record<string, number> {
  const areas: Record<string, number> = {};
  if (!data) return areas;

  const surfaceData = (data['BuildingSurface:Detailed'] || data['BuildingSurface'] || {}) as Record<
    string,
    {
      surface_type?: string;
      zone_name?: string;
      vertices?: Array<{
        vertex_x_coordinate?: unknown;
        vertex_y_coordinate?: unknown;
        vertex_z_coordinate?: unknown;
      }>;
    }
  >;

  for (const surface of Object.values(surfaceData)) {
    if (surface?.surface_type !== 'Floor') continue;
    const zoneName = surface.zone_name;
    if (!zoneName) continue;

    const verts = surface.vertices;
    if (!Array.isArray(verts) || verts.length < 3) continue;

    const points = verts.map((v) => ({
      x: Number(v.vertex_x_coordinate),
      y: Number(v.vertex_y_coordinate),
      z: Number(v.vertex_z_coordinate),
    }));
    if (points.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z))) {
      continue;
    }

    const area = polygonArea3D(points);
    areas[zoneName] = (areas[zoneName] || 0) + area;
  }

  return areas;
}

export function getZoneFloorArea(
  zoneName: string,
  zoneInfo: { floor_area?: unknown } | null | undefined,
  surfaceAreas: Record<string, number>
): number {
  const fromField = parseNumericValue(zoneInfo?.floor_area);
  if (fromField != null) return fromField;

  const fromSurfaces = surfaceAreas[zoneName];
  if (fromSurfaces != null && fromSurfaces > 0) return fromSurfaces;

  return 0;
}

export function lookupZoneFloorArea(
  zoneName: string,
  zoneData: Record<string, { floor_area?: unknown }>,
  surfaceAreas: Record<string, number>
): number {
  if (zoneData[zoneName]) {
    return getZoneFloorArea(zoneName, zoneData[zoneName], surfaceAreas);
  }

  const matchingZoneKey = Object.keys(zoneData).find(
    (k) => k === zoneName || k.endsWith(`:${zoneName}`) || k.includes(`:${zoneName}:`)
  );
  if (matchingZoneKey) {
    return getZoneFloorArea(matchingZoneKey, zoneData[matchingZoneKey], surfaceAreas);
  }

  return surfaceAreas[zoneName] || 0;
}

export function formatArea(area: unknown): string {
  const n = typeof area === 'number' ? area : parseNumericValue(area);
  if (n == null || !Number.isFinite(n)) return 'N/A';
  return n.toFixed(2);
}

export function formatZoneFloorAreaDisplay(
  zoneInfo: { floor_area?: unknown } | null | undefined,
  resolvedArea: number
): string {
  const raw = zoneInfo?.floor_area;
  if (raw === 0 || raw === '0') return '0';
  if (parseNumericValue(raw) != null) return String(raw);
  if (resolvedArea > 0) return resolvedArea.toFixed(2);
  if (raw != null && raw !== '') return String(raw);
  return '';
}
